import { useCallback, useEffect, useMemo, useState } from 'react';
import type QualityLevel from 'videojs-contrib-quality-levels/dist/types/quality-level';
import type QualityLevelList from 'videojs-contrib-quality-levels/dist/types/quality-level-list';

import type { XingPlayer } from '../types/xing-player';

const sortQualityLevels = (qualityLevels: QualityLevel[]): QualityLevel[] =>
  [...qualityLevels].sort((a, b) => (a.height ?? 0) - (b.height ?? 0));

const isFunction = (value: unknown): value is (...args: unknown[]) => unknown =>
  typeof value === 'function';

const isQualityLevel = (value: unknown): value is QualityLevel => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return 'id' in value && 'height' in value && 'width' in value;
};

const collectLevels = (list: QualityLevelList | null): QualityLevel[] => {
  if (!list) {
    return [];
  }

  const iterator = list[Symbol.iterator]();
  const levels: QualityLevel[] = [];
  let step = iterator.next();

  while (!step.done) {
    if (isQualityLevel(step.value)) {
      levels.push(step.value);
    }

    step = iterator.next();
  }

  return sortQualityLevels(levels);
};

const readSelectedLevel = (
  list: QualityLevelList | null
): QualityLevel | null => {
  if (!list) {
    return null;
  }

  const maybeSelectedIndex = Reflect.get(list, 'selectedIndex');

  if (typeof maybeSelectedIndex !== 'number' || maybeSelectedIndex < 0) {
    return null;
  }

  const candidate = Reflect.get(list, maybeSelectedIndex);

  if (isQualityLevel(candidate)) {
    return candidate;
  }

  return null;
};

const isQualityLevelList = (value: unknown): value is QualityLevelList => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const on = Reflect.get(value, 'on');
  const off = Reflect.get(value, 'off');

  return isFunction(on) && isFunction(off);
};

export const useQualityLevel = (
  player: XingPlayer['videojs']
): {
  qualityLevels: QualityLevel[];
  selectedQualityLevel: QualityLevel | null;
  selectQualityLevel: (id: string) => void;
} => {
  const [qualityLevels, setQualityLevels] = useState<QualityLevel[]>([]);
  const [selectedQualityLevel, setSelectedQualityLevel] =
    useState<QualityLevel | null>(null);

  const controller = useMemo(() => {
    const maybeQualityLevels = Reflect.get(player, 'qualityLevels');

    if (!isFunction(maybeQualityLevels)) {
      return null;
    }

    const result = maybeQualityLevels.apply(player);

    if (!isQualityLevelList(result)) {
      return null;
    }

    return result;
  }, [player]);

  useEffect(() => {
    if (!controller) {
      setQualityLevels([]);
      setSelectedQualityLevel(null);
      return undefined;
    }

    const syncLevels = (): void => {
      setQualityLevels(collectLevels(controller));
    };

    const syncSelection = (): void => {
      setSelectedQualityLevel(readSelectedLevel(controller));
    };

    const handleAddOrRemove = (): void => {
      syncLevels();
      syncSelection();
    };

    const handleChange = (): void => {
      syncSelection();
    };

    syncLevels();
    syncSelection();

    controller.on('addqualitylevel', handleAddOrRemove);
    controller.on('removequalitylevel', handleAddOrRemove);
    controller.on('change', handleChange);

    return () => {
      controller.off('addqualitylevel', handleAddOrRemove);
      controller.off('removequalitylevel', handleAddOrRemove);
      controller.off('change', handleChange);
    };
  }, [controller]);

  return {
    qualityLevels,
    selectedQualityLevel,
    selectQualityLevel: useCallback(
      (id: string): void => {
        if (!controller) {
          return;
        }

        const levels = collectLevels(controller);
        const nextSelected =
          controller.getQualityLevelById(id) ??
          levels.find((qualityLevel) => qualityLevel.id === id) ??
          null;

        if (!nextSelected) {
          return;
        }

        levels.forEach((qualityLevel) => {
          Reflect.set(qualityLevel, 'enabled', qualityLevel.id === id);
        });

        setQualityLevels(levels);
        setSelectedQualityLevel(nextSelected);
      },
      [controller]
    ),
  };
};
